/** Color-scheme presets a tenant (or a viewer) can pick; "default" needs no stylesheet. */
export const THEME_PRESET_VALUES = ["default", "tangerine", "soft-pop", "brutalist"] as const;
export type ThemePreset = (typeof THEME_PRESET_VALUES)[number];

/** Narrows a tenant's stored `theme_preset` to a known preset, or `null` (use the app default). */
export function toThemePreset(value: string | null | undefined): ThemePreset | null {
  return THEME_PRESET_VALUES.find((preset) => preset === value) ?? null;
}

/**
 * Where a tenant's image (logo or card artwork) is served from. A stored path starting with "/" is a file served by the app
 * itself (the original tenant's bundled logo); a full URL is used as is; anything else is an object
 * path inside the public `tenant-assets` bucket. `null` means the tenant has none.
 */
export function logoUrl(logoPath: string | null | undefined, supabaseUrl: string): string | null {
  if (!logoPath) {
    return null;
  }
  if (logoPath.startsWith("/") || /^https?:\/\//i.test(logoPath)) {
    return logoPath;
  }
  const base = supabaseUrl.replace(/\/+$/, "");
  const path = logoPath.replace(/^\/+/, "").split("/").map(encodeURIComponent).join("/");
  return `${base}/storage/v1/object/public/tenant-assets/${path}`;
}

/** Up to two capital letters for a tenant with no logo: "Acme Fuel" → "AF", "gas" → "G". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? words.slice(0, 2).map((w) => w[0]) : [words[0]?.[0] ?? "?"];
  return letters.join("").toUpperCase();
}
